import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import ApplePayLogo from 'components/Common/Logos/ApplePayLogo'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import LoyaltyProgramNameLogo from 'loyalty/components/LoyaltyProgramNameLogo'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { APPLE_PAY_URL, LOYALTY_CREDIT_CARD_COLOUR_SLATE, LOYALTY_CREDIT_CARD_LAYOUT_CONTAINER_CLASS } from './loyaltyCreditCardConstants'

const Section = styled(Pane)`
  /* Figma spec: charcoal slate, a raw hex in the design with no palette token.
     The Pane renders transparent (background="none") and the colour is painted
     here; &&& outranks Sheet's background modifier class. */
  &&& {
    background-color: ${LOYALTY_CREDIT_CARD_COLOUR_SLATE};
  }

  padding: ${rem(40)} 0;

  ${mediaQueryUp.tablet} {
    margin-bottom: 0;
    padding-bottom: 0;
    padding-top: ${rem(60)};
  }
`

const DarkSplitSection = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(40)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: ${rem(17)};
    align-items: center;
  }
`

const DarkSplitImage = styled.div`
  width: 100%;
  background: transparent;

  picture {
    display: block;
    width: 100%;
  }

  img {
    width: 100%;
    height: auto;
    display: block;
  }

  ${mediaQueryUp.tablet} {
    border-radius: ${rem(8)};
    overflow: hidden;
    height: ${rem(514)};

    picture {
      height: 100%;
    }

    img {
      height: 100%;
      object-fit: cover;
    }
  }
`

const DarkText = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${rem(32)};
`

const ApplePayLearnMoreButton = styled(TextButton)`
  align-self: flex-start;

  /* Ghost text buttons never set --button-padding-y, so BaseButton's outdent
     falls back to a unitless 0, which invalidates its translate() calc and the
     horizontalOutdent silently no-ops. Giving the block amount a unit restores
     the outdent. Root cause belongs in LuxKit BaseButton (fallback 0 -> 0px). */
  --button-outdent-block-amount: 0px;
`

const BrandLogos = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(16)};
`

const SocieteMastercardLogo = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(12)};
  color: white;
`

const BrandSeparator = styled.span`
  display: inline-block;
  width: 1px;
  height: ${rem(32)};
  background: rgba(255, 255, 255, 0.4);
  flex-shrink: 0;
`

const SocieteWordmark = styled(LoyaltyProgramNameLogo)`
  height: ${rem(28)};
  width: auto;
  position: relative;
  top: ${rem(-2)};
`

const InlineApplePayLogo = styled.span`
  display: inline-flex;
  align-items: center;
  vertical-align: middle;

  svg {
    height: ${rem(16)};
    width: auto;
  }
`

const LockupApplePayMark = styled(ApplePayLogo)`
  height: ${rem(36)};
  width: auto;
`

function LoyaltyCreditCardApplePaySection() {
  const intl = useIntl()

  return (
    <Section background="none">
      <LayoutContainer className={LOYALTY_CREDIT_CARD_LAYOUT_CONTAINER_CLASS} variant="large">
        <DarkSplitSection>
          <DarkSplitImage>
            <ResponsiveImage
              id="societe-apple-pay-phone"
              format="png"
              fit="center"
              objectFit="cover"
              quality="best"
            />
          </DarkSplitImage>
          <DarkText>
            <BrandLogos>
              <SocieteMastercardLogo>
                <SocieteWordmark
                  role="img"
                  aria-label={intl.formatMessage({
                    id: 'vbUUjMwdBq0wGlLSu-6a',
                    defaultMessage: 'Société',
                    description: 'Accessible label for the Société wordmark logo shown in the Apple Pay section of the credit card page',
                  })}
                />
                <BrandSeparator aria-hidden="true" />
                <LockupApplePayMark
                  role="img"
                  aria-label={intl.formatMessage({
                    id: '7XcmhiKUr64QtRijRwyg',
                    defaultMessage: 'Apple Pay',
                    description: 'Accessible label for the Apple Pay mark in the Société Credit Card Apple Pay section',
                  })}
                />
              </SocieteMastercardLogo>
            </BrandLogos>
            <Heading variant="heading1" colour="static-white">
              {intl.formatMessage({
                id: 'O459XvCQoE2ukahvHnhk',
                defaultMessage: 'Save time, every time. Check out faster with Apple Pay.',
                description: 'Apple Pay section heading on Société Credit Card page',
              })}
            </Heading>
            <BodyText variant="large" colour="static-white">
              {intl.formatMessage(
                {
                  id: 'kQIkCD6fdCSBWOSh835M',
                  defaultMessage: 'Apple Pay makes everyday purchases with your Luxury Escapes Société Credit Card seamless. Whether you’re making a morning coffee run or shopping for a pair of fresh new kicks. Just look for {appleLogo} or the contactless symbol at check-out.',
                  description: 'Apple Pay section body on Société Credit Card page',
                },
                {
                  appleLogo: (
                    <InlineApplePayLogo key="apple-pay-logo">
                      <ApplePayLogo
                        role="img"
                        aria-label={intl.formatMessage({
                          id: '7XcmhiKUr64QtRijRwyg',
                          defaultMessage: 'Apple Pay',
                          description: 'Accessible label for the Apple Pay mark in the Société Credit Card Apple Pay section',
                        })}
                      />
                    </InlineApplePayLogo>
                  ),
                },
              )}
            </BodyText>
            <ApplePayLearnMoreButton kind="tertiary" variant="ghost" size="large" horizontalOutdent="start" href={APPLE_PAY_URL} target="_blank" rel="noopener noreferrer">
              {intl.formatMessage({
                id: 'cBGm8EZB8CWHR9aaEEUq',
                defaultMessage: 'Learn more',
                description: 'Apple Pay learn more link on Société Credit Card page',
              })}
            </ApplePayLearnMoreButton>
          </DarkText>
        </DarkSplitSection>
      </LayoutContainer>
    </Section>
  )
}

export default LoyaltyCreditCardApplePaySection
